import { getStatusBadgeClass } from "../components/StatusBadge";

export type OrderLike = {
  id?: string;
  type?: string | null;
  status?: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
  special_note?: string | null;
  customer?: { name?: string | null; phone?: string | null } | null;
  rider?: { lalamove_order_id?: string | null; lalamove_status?: string | null } | null;
};

export const ORDER_STATUS_THEME: Record<string, { badge: string; action: string }> = {
  new: {
    badge: `${getStatusBadgeClass("new")} ring-1`,
    action: `${getStatusBadgeClass("new")} ring-1`,
  },
  pending: {
    badge: `${getStatusBadgeClass("pending")} ring-1`,
    action: `${getStatusBadgeClass("pending")} ring-1`,
  },
  confirmed: {
    badge: `${getStatusBadgeClass("confirmed")} ring-1`,
    action: `${getStatusBadgeClass("confirmed")} ring-1`,
  },
  preparing: {
    badge: `${getStatusBadgeClass("preparing")} ring-1`,
    action: "bg-[#ea580c] text-white shadow-[0_8px_18px_rgba(234,88,12,0.18)]",
  },
  ready: {
    badge: `${getStatusBadgeClass("ready")} ring-1`,
    action: "bg-[#006c49] text-white shadow-[0_8px_18px_rgba(0,108,73,0.18)]",
  },
  out_for_delivery: {
    badge: `${getStatusBadgeClass("out_for_delivery")} ring-1`,
    action: "bg-[#0369a1] text-white shadow-[0_8px_18px_rgba(3,105,161,0.18)]",
  },
  in_transit: {
    badge: `${getStatusBadgeClass("in_transit")} ring-1`,
    action: "bg-[#075985] text-white shadow-[0_8px_18px_rgba(7,89,133,0.18)]",
  },
  delivered: {
    badge: `${getStatusBadgeClass("delivered")} ring-1`,
    action: `${getStatusBadgeClass("delivered")} ring-1`,
  },
  completed: {
    badge: `${getStatusBadgeClass("completed")} ring-1`,
    action: `${getStatusBadgeClass("completed")} ring-1`,
  },
  cancelled: {
    badge: `${getStatusBadgeClass("cancelled")} ring-1`,
    action: `${getStatusBadgeClass("cancelled")} ring-1`,
  },
};

export const FALLBACK_ORDER_STATUS_THEME = {
  badge: `${getStatusBadgeClass("")} ring-1`,
  action: `${getStatusBadgeClass("")} ring-1`,
};

export const TERMINAL_ORDER_STATUSES = new Set(["completed", "delivered", "cancelled"]);
export const CANCEL_HIDDEN_ORDER_STATUSES = new Set(["cancelled", "completed", "delivered"]);
export const READY_OR_LATER_ORDER_STATUSES = new Set(["ready", "out_for_delivery", "in_transit", "delivered", "completed"]);

export const ORDER_ACTION_BUTTON_CLASS: Record<string, string> = {
  mark_ready: "bg-[#EA580C] text-white ring-1 ring-[#EA580C] shadow-[0 1px 2px 0 rgba(0, 0, 0, 0.05)]",
  picked_up: "bg-[#6C4C00] text-white ring-1 ring-[#6C4C00] shadow-[0 1px 2px 0 rgba(0, 0, 0, 0.05)]",
  out_for_delivery: "bg-[#F59E0B] text-white ring-1 ring-[#F59E0B] shadow-[0 1px 2px 0 rgba(0, 0, 0, 0.05)]",
  delivered: "bg-[#006C49] text-white ring-1 ring-[#006C49] shadow-[0 1px 2px 0 rgba(0, 0, 0, 0.05)]",
};

export const ORDER_ACTION_LABEL_KEY: Record<string, string> = {
  mark_ready: "orders.actionMarkReady",
  picked_up: "orders.actionPickedUp",
  out_for_delivery: "orders.actionOutForDelivery",
  delivered: "orders.actionDelivered",
};

export const ORDER_DETAIL_ACTION_LABEL_KEY: Record<string, string> = {
  mark_ready: "orderDetail.markAsReady",
  picked_up: "orderDetail.pickedUp",
  out_for_delivery: "orderDetail.outForDelivery",
  delivered: "orderDetail.markDelivered",
};

export function getOrderStatusTheme(status: string | null | undefined) {
  return ORDER_STATUS_THEME[normalizeOrderStatus(status)] ?? FALLBACK_ORDER_STATUS_THEME;
}

export function getOrderCustomerDisplay(order: OrderLike) {
  const contactPart = order.special_note?.split("—")[0]?.trim();
  const walkInMatch = contactPart?.match(/^Walk-in:\s*(.+?)(?:\s*\((.+)\))?$/i);
  return {
    name: order.customer?.name ?? order.customer_name ?? walkInMatch?.[1]?.trim() ?? null,
    phone: order.customer?.phone ?? order.customer_phone ?? walkInMatch?.[2]?.trim() ?? null,
  };
}

export function getOrderStatusAction(order: OrderLike) {
  const status = normalizeOrderStatus(order.status);
  const type = order.type;
  if (status === "preparing") return { actionKey: "mark_ready", nextStatus: "ready" };
  if (status === "ready" && type === "pickup") return { actionKey: "picked_up", nextStatus: "completed" };
  if (status === "ready" && type === "delivery") {
    // A booked Lalamove rider flips the order to "out for delivery" by itself at pickup — the server refuses a manual dispatch before
    // that, so offer no dead button. While the rider is searching / heading to the shop / failed, the rider card (and its 重新提交) is the next step.
    if (order.rider?.lalamove_order_id && !["PICKED_UP", "COMPLETED"].includes(order.rider.lalamove_status ?? "")) return null;
    return { actionKey: "out_for_delivery", nextStatus: "out_for_delivery" };
  }
  if (status === "out_for_delivery" || status === "in_transit") {
    // A Lalamove-tracked delivery can only be handed over once the rider has the food.
    if (order.rider?.lalamove_order_id && !["PICKED_UP", "COMPLETED"].includes(order.rider.lalamove_status ?? "")) return null;
    return { actionKey: "delivered", nextStatus: "delivered" };
  }
  return null;
}

export function normalizeOrderStatus(status: string | null | undefined): string {
  const normalized = String(status ?? "").trim();
  const lower = normalized.replace(/-/g, "_").replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
  return lower.replace(/^_/, "");
}

/** i18n key (orders.*) describing a Lalamove rider status in plain words. */
export function lalamoveStatusKey(status: string | null | undefined): string {
  switch (status) {
    case "ON_GOING": return "orders.lmOnGoing";
    case "PICKED_UP": return "orders.lmPickedUp";
    case "COMPLETED": return "orders.lmCompleted";
    case "CANCELED": case "REJECTED": case "EXPIRED": return "orders.lmFailed";
    default: return "orders.lmAssigning";
  }
}
